import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Settings, Target, TrendingUp, AlertTriangle, Plus, BarChart3, Gauge, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';

const ALIGNMENT_LEVELS = [
  { value: 'TOTAL', label: 'Alinhamento Total', color: 'bg-green-500' },
  { value: 'PARCIAL', label: 'Alinhamento Parcial', color: 'bg-yellow-500' },
  { value: 'NENHUM', label: 'Sem Alinhamento', color: 'bg-red-500' },
  { value: 'NAO_APLICAVEL', label: 'Não Aplicável', color: 'bg-gray-500' },
];

const RISK_LEVELS = [
  { value: 'BAIXO', label: 'Baixo', color: 'bg-green-500' },
  { value: 'MEDIO', label: 'Médio', color: 'bg-yellow-500' },
  { value: 'ALTO', label: 'Alto', color: 'bg-orange-500' },
  { value: 'CRITICO', label: 'Crítico', color: 'bg-red-500' },
];

const STRATEGIC_AREAS = [
  'Governança de TI',
  'Segurança da Informação',
  'Infraestrutura',
  'Sistemas de Informação',
  'Gestão de Pessoas',
  'Inovação e Transformação Digital',
];

export default function GovernancePage() {
  const { api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showObjectiveDialog, setShowObjectiveDialog] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [showIndicatorDialog, setShowIndicatorDialog] = useState(false);

  const [newObjective, setNewObjective] = useState({
    code: '',
    title: '',
    description: '',
    strategic_area: 'Governança de TI',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    responsible_unit: '',
  });

  const [newMapping, setNewMapping] = useState({
    act_id: '',
    objective_id: '',
    alignment_level: 'PARCIAL',
    justification: '',
    operational_risk: 'MEDIO',
    risk_description: '',
  });

  const [newIndicator, setNewIndicator] = useState({
    name: '',
    description: '',
    formula: '',
    target_value: 100,
    current_value: 0,
    unit: '%',
    frequency: 'Mensal',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashRes, objRes, mapRes, indRes] = await Promise.all([
        api.get('/governance/dashboard'),
        api.get('/governance/pdtic/objectives'),
        api.get('/governance/pdtic/mappings'),
        api.get('/governance/indicators'),
      ]);
      setDashboard(dashRes.data);
      setObjectives(objRes.data.objectives || []);
      setMappings(mapRes.data.mappings || []);
      setIndicators(indRes.data.indicators || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de governança');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateObjective = async () => {
    try {
      await api.post('/governance/pdtic/objectives', {
        ...newObjective,
        start_date: new Date(newObjective.start_date).toISOString(),
        end_date: new Date(newObjective.end_date).toISOString(),
      });
      toast.success('Objetivo PDTIC criado com sucesso');
      setShowObjectiveDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar objetivo');
    }
  };

  const handleCreateMapping = async () => {
    try {
      await api.post('/governance/pdtic/mappings', newMapping);
      toast.success('Mapeamento criado com sucesso');
      setShowMappingDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar mapeamento');
    }
  };

  const handleCreateIndicator = async () => {
    try {
      await api.post('/governance/indicators', newIndicator);
      toast.success('Indicador criado com sucesso');
      setShowIndicatorDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar indicador');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getIndicatorStatusColor = (status) => {
    switch (status) {
      case 'ON_TRACK': return 'bg-green-500';
      case 'AT_RISK': return 'bg-yellow-500';
      case 'OFF_TRACK': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="space-y-6" data-testid="governance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="h-6 w-6 text-indigo-600" />
            Governança & PDTIC
          </h1>
          <p className="text-slate-500">Alinhamento estratégico com o Plano Diretor de TIC</p>
        </div>
      </div>

      {/* Score e Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600 font-medium">Score de Governança</p>
                <p className={`text-3xl font-bold ${getScoreColor(dashboard?.governance_score || 0)}`}>
                  {dashboard?.governance_score || 0}%
                </p>
              </div>
              <Gauge className="h-10 w-10 text-indigo-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cobertura PDTIC</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard?.mapping_coverage || 0}%</p>
              </div>
              <Target className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Alto Risco</p>
                <p className="text-2xl font-bold text-red-600">{dashboard?.high_risk_count || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Indicadores na Meta</p>
                <p className="text-2xl font-bold text-green-600">
                  {dashboard?.indicators?.summary?.on_track || 0}/{dashboard?.indicators?.summary?.total || 0}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recomendações */}
      {dashboard?.recommendations?.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
              <AlertTriangle className="h-5 w-5" />
              Pontos de Atenção
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {dashboard.recommendations.map((rec, i) => (
                <li key={i} className="text-sm text-orange-700 flex items-start gap-2">
                  <span>•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="objectives" className="space-y-4">
        <TabsList>
          <TabsTrigger value="objectives" data-testid="tab-objectives">
            <Target className="h-4 w-4 mr-2" />
            Objetivos PDTIC
          </TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">
            <Settings className="h-4 w-4 mr-2" />
            Mapeamentos
          </TabsTrigger>
          <TabsTrigger value="indicators" data-testid="tab-indicators">
            <BarChart3 className="h-4 w-4 mr-2" />
            Indicadores
          </TabsTrigger>
        </TabsList>

        {/* Objetivos PDTIC */}
        <TabsContent value="objectives">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Objetivos Estratégicos do PDTIC</CardTitle>
                <CardDescription>Metas e objetivos do Plano Diretor de TIC</CardDescription>
              </div>
              <Dialog open={showObjectiveDialog} onOpenChange={setShowObjectiveDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-new-objective">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Objetivo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Novo Objetivo PDTIC</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Código</Label>
                        <Input
                          value={newObjective.code}
                          onChange={(e) => setNewObjective({ ...newObjective, code: e.target.value })}
                          placeholder="OBJ-001"
                        />
                      </div>
                      <div>
                        <Label>Área Estratégica</Label>
                        <Select
                          value={newObjective.strategic_area}
                          onValueChange={(v) => setNewObjective({ ...newObjective, strategic_area: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STRATEGIC_AREAS.map((area) => (
                              <SelectItem key={area} value={area}>{area}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Título</Label>
                      <Input
                        value={newObjective.title}
                        onChange={(e) => setNewObjective({ ...newObjective, title: e.target.value })}
                        placeholder="Título do objetivo..."
                      />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea
                        value={newObjective.description}
                        onChange={(e) => setNewObjective({ ...newObjective, description: e.target.value })}
                        placeholder="Descrição detalhada do objetivo..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Data Início</Label>
                        <Input
                          type="date"
                          value={newObjective.start_date}
                          onChange={(e) => setNewObjective({ ...newObjective, start_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Data Fim</Label>
                        <Input
                          type="date"
                          value={newObjective.end_date}
                          onChange={(e) => setNewObjective({ ...newObjective, end_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Unidade Responsável</Label>
                      <Input
                        value={newObjective.responsible_unit}
                        onChange={(e) => setNewObjective({ ...newObjective, responsible_unit: e.target.value })}
                        placeholder="Ex: DTI, CGTI..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowObjectiveDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateObjective}>Salvar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {objectives.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        Nenhum objetivo cadastrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    objectives.map((obj) => (
                      <TableRow key={obj.objective_id}>
                        <TableCell className="font-mono font-bold">{obj.code}</TableCell>
                        <TableCell className="font-medium">{obj.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{obj.strategic_area}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(obj.start_date).toLocaleDateString('pt-BR')} - {new Date(obj.end_date).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-sm">{obj.responsible_unit}</TableCell>
                        <TableCell>
                          <Badge variant="default">{obj.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mapeamentos */}
        <TabsContent value="mappings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Mapeamento Atos x PDTIC</CardTitle>
                <CardDescription>Alinhamento dos atos de controle com os objetivos do PDTIC</CardDescription>
              </div>
              <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-new-mapping">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Mapeamento
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Mapear Ato ao PDTIC</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>ID do Ato</Label>
                        <Input
                          value={newMapping.act_id}
                          onChange={(e) => setNewMapping({ ...newMapping, act_id: e.target.value })}
                          placeholder="act_xxx"
                        />
                      </div>
                      <div>
                        <Label>Objetivo PDTIC</Label>
                        <Select
                          value={newMapping.objective_id}
                          onValueChange={(v) => setNewMapping({ ...newMapping, objective_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {objectives.map((obj) => (
                              <SelectItem key={obj.objective_id} value={obj.objective_id}>
                                {obj.code} - {obj.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nível de Alinhamento</Label>
                        <Select
                          value={newMapping.alignment_level}
                          onValueChange={(v) => setNewMapping({ ...newMapping, alignment_level: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ALIGNMENT_LEVELS.map((level) => (
                              <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Risco Operacional</Label>
                        <Select
                          value={newMapping.operational_risk}
                          onValueChange={(v) => setNewMapping({ ...newMapping, operational_risk: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RISK_LEVELS.map((risk) => (
                              <SelectItem key={risk.value} value={risk.value}>{risk.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Justificativa</Label>
                      <Textarea
                        value={newMapping.justification}
                        onChange={(e) => setNewMapping({ ...newMapping, justification: e.target.value })}
                        placeholder="Justifique o nível de alinhamento..."
                      />
                    </div>
                    <div>
                      <Label>Descrição do Risco</Label>
                      <Textarea
                        value={newMapping.risk_description}
                        onChange={(e) => setNewMapping({ ...newMapping, risk_description: e.target.value })}
                        placeholder="Descreva os riscos operacionais identificados..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowMappingDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateMapping}>Salvar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ato</TableHead>
                    <TableHead>Objetivo PDTIC</TableHead>
                    <TableHead>Alinhamento</TableHead>
                    <TableHead>Risco</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        Nenhum mapeamento cadastrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    mappings.map((m) => (
                      <TableRow key={m.mapping_id}>
                        <TableCell className="font-mono text-sm">{m.act_id}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{m.objective_code}</span>
                          <span className="ml-2 text-sm">{m.objective_title}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={ALIGNMENT_LEVELS.find(l => l.value === m.alignment_level)?.color}>
                            {ALIGNMENT_LEVELS.find(l => l.value === m.alignment_level)?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={RISK_LEVELS.find(r => r.value === m.operational_risk)?.color}>
                            {RISK_LEVELS.find(r => r.value === m.operational_risk)?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(m.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Indicadores */}
        <TabsContent value="indicators">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Indicadores Operacionais</CardTitle>
                <CardDescription>Métricas de desempenho e acompanhamento</CardDescription>
              </div>
              <Dialog open={showIndicatorDialog} onOpenChange={setShowIndicatorDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-new-indicator">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Indicador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Indicador Operacional</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div>
                      <Label>Nome do Indicador</Label>
                      <Input
                        value={newIndicator.name}
                        onChange={(e) => setNewIndicator({ ...newIndicator, name: e.target.value })}
                        placeholder="Ex: Taxa de Resolução de Atos"
                      />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea
                        value={newIndicator.description}
                        onChange={(e) => setNewIndicator({ ...newIndicator, description: e.target.value })}
                        placeholder="Descrição do indicador..."
                      />
                    </div>
                    <div>
                      <Label>Fórmula</Label>
                      <Input
                        value={newIndicator.formula}
                        onChange={(e) => setNewIndicator({ ...newIndicator, formula: e.target.value })}
                        placeholder="Ex: (Atos Resolvidos / Total de Atos) x 100"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Meta</Label>
                        <Input
                          type="number"
                          value={newIndicator.target_value}
                          onChange={(e) => setNewIndicator({ ...newIndicator, target_value: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Valor Atual</Label>
                        <Input
                          type="number"
                          value={newIndicator.current_value}
                          onChange={(e) => setNewIndicator({ ...newIndicator, current_value: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <Label>Unidade</Label>
                        <Select
                          value={newIndicator.unit}
                          onValueChange={(v) => setNewIndicator({ ...newIndicator, unit: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="%">%</SelectItem>
                            <SelectItem value="dias">Dias</SelectItem>
                            <SelectItem value="qtd">Quantidade</SelectItem>
                            <SelectItem value="R$">R$</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Frequência de Medição</Label>
                      <Select
                        value={newIndicator.frequency}
                        onValueChange={(v) => setNewIndicator({ ...newIndicator, frequency: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Semanal">Semanal</SelectItem>
                          <SelectItem value="Mensal">Mensal</SelectItem>
                          <SelectItem value="Trimestral">Trimestral</SelectItem>
                          <SelectItem value="Anual">Anual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowIndicatorDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateIndicator}>Salvar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {indicators.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    Nenhum indicador cadastrado
                  </div>
                ) : (
                  indicators.map((ind) => (
                    <Card key={ind.indicator_id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{ind.name}</h3>
                            <Badge className={getIndicatorStatusColor(ind.status)}>
                              {ind.status === 'ON_TRACK' ? 'Na Meta' : (ind.status === 'AT_RISK' ? 'Em Risco' : 'Fora da Meta')}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{ind.description}</p>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span>Progresso: {ind.current_value}{ind.unit} / {ind.target_value}{ind.unit}</span>
                              <span className="font-medium">{ind.performance_pct}%</span>
                            </div>
                            <Progress value={Math.min(ind.performance_pct, 100)} className="h-2" />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </AppLayout>
  );
}
